// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { XCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

/**
 * The label on a finding rejected in this review, under its title.
 *
 * The card stays in its place, grayed, so she can see what she threw out and
 * undo it; the label says why it is gray. Who comes from `likedBy` (the last
 * person to judge it, which for a rejected card is the rejecter). No time is
 * shown: christo stores no rejection timestamp, and the comment's `updatedAt`
 * moves on every later write, so it would date the wrong event.
 *
 * Rendered on the collapsed and the open card alike, which are mutually
 * exclusive per comment, so the testid stays unique on the page.
 */
export function AskV2ReviewRejectedHereSubheading({
  comment,
  commentId,
  authorNameByUserId,
  currentUserId,
  currentUserName,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
}) {
  const { t } = useTranslation("ui");
  if (comment.liked !== false || comment.isDeleted) return null;
  const rejecter = comment.likedBy?.trim();
  const name =
    (rejecter ? authorNameByUserId?.[rejecter]?.trim() : undefined) ||
    (rejecter && rejecter === currentUserId
      ? currentUserName?.trim()
      : undefined);
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      className="mt-1 flex items-center gap-1.5 text-puntt-neutral-gray-11"
      data-testid={`ask-v2-review-comment-rejected-${commentId}`}
    >
      <XCircle size={16} weight="bold" aria-hidden="true" />
      {name
        ? t("askV2.review.rejectedHereBy", { name })
        : t("askV2.review.rejectedHere")}
    </Text>
  );
}
